import { useEffect, type RefObject } from 'react'

type ColorFn = (nx: number, ny: number) => [number, number, number, number]

interface BayerDitherOptions {
  pixelSize?: number
  color?: ColorFn
}

const BAYER = [
  0/16, 8/16, 2/16, 10/16,
  12/16, 4/16, 14/16, 6/16,
  3/16, 11/16, 1/16, 9/16,
  15/16, 7/16, 13/16, 5/16,
]

const DEFAULT_COLOR: ColorFn = (_nx, _ny) => [167, 139, 250, 80]

export function useBayerDither(canvasRef: RefObject<HTMLCanvasElement | null>, options: BayerDitherOptions = {}) {
  const { pixelSize = 4, color = DEFAULT_COLOR } = options

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let time = 0
    let animId: number
    let img: ImageData | null = null

    function resize() {
      const parent = canvas?.parentElement
      if (!parent) return
      const rect = parent.getBoundingClientRect()
      canvas!.width = Math.ceil(rect.width / pixelSize)
      canvas!.height = Math.ceil(rect.height / pixelSize)
      img = ctx!.createImageData(canvas!.width, canvas!.height)
    }

    function render() {
      time += 0.006
      const w = canvas!.width
      const h = canvas!.height
      if (w === 0 || h === 0 || !img) {
        animId = requestAnimationFrame(render)
        return
      }
      const d = img.data

      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const nx = x / w
          const ny = y / h
          const wave =
            Math.sin(nx * 8 + time * 1.2) * 0.25 +
            Math.sin(ny * 6 - time * 0.8) * 0.2 +
            Math.sin((nx * 3 + ny * 4) + time) * 0.15
          const brightness = 0.35 + wave
          const threshold = BAYER[(y % 4) * 4 + (x % 4)]
          const on = brightness > threshold

          const i = (y * w + x) * 4
          const [r, g, b, a] = color(nx, ny)
          d[i]     = on ? r : 0
          d[i + 1] = on ? g : 0
          d[i + 2] = on ? b : 0
          d[i + 3] = on ? a : 0
        }
      }

      ctx!.putImageData(img, 0, 0)
      animId = requestAnimationFrame(render)
    }

    resize()
    window.addEventListener('resize', resize)
    render()

    return () => {
      cancelAnimationFrame(animId)
      window.removeEventListener('resize', resize)
    }
  }, [canvasRef, pixelSize, color])
}
